import { useEffect, useState } from "react";
import { tipsApi } from "../services/tips";
import type { SignupNotice } from "../types/auth";

// The two app-tips boxes the sign-in screen has words for (Step1Welcome), and
// no box: where the law needs no choice at sign-up (the US, Canada, Australia,
// New Zealand) and until the server has answered.
export const OPT_OUT_BOX: SignupNotice = { notice: "signup-2026-10", kind: "opt_out" };
export const OPT_IN_BOX: SignupNotice = { notice: "signup-2026-10-optin", kind: "opt_in" };
export const NO_BOX: SignupNotice = { notice: null, kind: "none" };

// The server's last answer for this phone's timezone, kept for the tab's
// session: back on the screen (Back from the profile step, a reload) the right
// box, or none, is there from the first paint.
const SESSION_KEY = "rq_signup_tips_box";

function phoneTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

// Only an answer this screen has words for; anything else gets the default box.
const known = (answer: Partial<SignupNotice> | null | undefined): SignupNotice => {
  if (answer?.kind === NO_BOX.kind) return NO_BOX;
  return answer?.kind === OPT_IN_BOX.kind && answer?.notice === OPT_IN_BOX.notice ? OPT_IN_BOX : OPT_OUT_BOX;
};

// null = nothing kept for this timezone: no box until the server answers.
function remembered(tz: string): SignupNotice | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    if (saved && saved.tz === tz) return known(saved);
  } catch {
    // No storage here: no box until the server answers.
  }
  return null;
}

/**
 * Which app-tips box the sign-in screen shows, by the server's answer for this
 * phone's timezone (GET /tips/signup-notice): "Don't email me app tips" (Europe,
 * the UK and most countries), "Email me app tips" where the law wants an
 * explicit yes, NO_BOX where it needs no choice at sign-up. No box until the
 * answer is in, so none flashes up and away; when the server can't answer, the
 * default "Don't email me app tips". Every one is safe: with no box the sign-up
 * sends no answer, and the server re-checks the talent's country before any
 * tips email.
 */
export function useSignupTipsBox(): SignupNotice {
  // null = the server hasn't answered yet.
  const [box, setBox] = useState<SignupNotice | null>(() => remembered(phoneTimezone()));

  useEffect(() => {
    let alive = true;
    const tz = phoneTimezone();
    Promise.resolve()
      .then(() => tipsApi.signupNotice(tz))
      .then(
        (answer) => {
          const next = known(answer);
          try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify({ tz, ...next }));
          } catch {
            // Remembering is a nicety.
          }
          if (alive) setBox(next);
        },
        () => {
          // No answer: the default box, unless this session already had one.
          if (alive) setBox((shown) => shown || OPT_OUT_BOX);
        }
      );
    return () => {
      alive = false;
    };
  }, []);

  return box || NO_BOX;
}
